import React from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Typography, Theme } from '@mui/material'
import { makeStyles } from '@mui/styles'

import { CalendarDayObject, dndStateProps } from '../PostCalendar'
import { CalendarDateParams, DateObject, PostObject } from '../ProfessocialCalendar'
import PostCard from './PostCard'
import { AboType, PostContent, NetworkType } from '@3m5/sm_publish-frontend-api'
import { useSelector } from 'react-redux'
import { getUser, namedRequestData } from '../../../../../store/common/common.selectors'
import formatDate from '@professocial/professocialFormComponents/FormComponents/util/formatDate'
import { datesAreOnSameDay } from '../utils/datesAreTheSame'
import { getCalendarDataInitiated } from '../../../../../store/dashboard/dashboard.actions'
import { CoreRootState } from '../../../../../store/coreRoot.reducer'
import { ActionCall } from '@professocial/professocialTypes/types/commonTypes'
import Preloader from '@professocial/professocialFormComponents/FormComponents/Preloader'

interface IProps {
  index: number
  day: CalendarDayObject
  selectedDate?: number
  calendarData?: DateObject
  dragAndDrop: dndStateProps
  onHandleDnD: (data: dndStateProps) => void
  editSettings?: (topicId: string | undefined, aboType: AboType) => void
  setSelectedDate?: (selectedDate: number) => void
  changeCalendarData?: (data: DateObject) => void
  changeSelectedDate?: (date: number) => void
  changeDataParams?: (params: CalendarDateParams) => void
}

export const isRestricted = (to: number): boolean => new Date(to) <= new Date(Date.now())

const CalendarDay: React.FC<IProps> = ({ day, index, dragAndDrop, ...props }) => {
  const { t } = useTranslation()

  const loadingCalendarData: ActionCall | undefined = useSelector((state: CoreRootState) => namedRequestData(state, getCalendarDataInitiated.type))
  const user = useSelector(getUser)

  const position = day && new Date(day.year, day.month - 1, day.day).getTime()

  const useStyles = makeStyles((theme: Theme) => ({
    calendarDaysDay: {
      display: 'flex',
      flexDirection: 'column',
      width: '15%',
      maxWidth: '15%',
      minHeight: '50px',
      height: 'auto',
      borderRight: '1px solid #f5f5f5',
      backgroundColor: ((index + 1) % 2 === 0) ? '#FFFFFF' : '#fdfdfd',
      transition: 'all .2s ease-in-out',
    },
    dayNumber: {
      padding: '10px 12px',
      textAlign: 'right',
      color: day.actualMonth ? (day.actualDay ? '#646464' : '#B1B1B1') : '#EBEBEB',
      fontWeight: day.actualDay ? 700 : 500,
    },
    postCards: {},
    dropArea: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#ffffff',
      position: 'relative',
      padding: '0px !important',
      '&::before': {
        content: `'${t('coreCalendar:drop')}'`,
        color: '#646464',
        fontSize: '.7em',
        textTransform: 'uppercase',
        padding: '14px',
        width: '100%',
        height: '100%',
        border: '2px dashed rgba(100, 100, 100, 0.5)',
        position: 'absolute',
        display: 'flex',
        justifyContent: 'flex-start',
      },
    },
    dropAreaRestricted: {
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      padding: '0px !important',
      '&::before': {
        content: '\'×\'',
        color: 'red',
        fontSize: '1.3em',
        textTransform: 'uppercase',
        padding: '5px 12px',
        width: '100%',
        height: '100%',
        position: 'absolute',
        display: 'flex',
        justifyContent: 'flex-start',
      },
    },
  }))

  const classes = useStyles()

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const draggedTo = Number(e.currentTarget.dataset.position)

    if (draggedTo !== dragAndDrop.draggedTo && dragAndDrop.draggedItemData !== undefined && !isRestricted(draggedTo)) {
      const { postStart } = dragAndDrop.draggedItemData
      const newStartDate = postStart && new Date(draggedTo).setHours(postStart.getHours(), postStart.getMinutes(), postStart.getSeconds())

      props.onHandleDnD({
        ...dragAndDrop,
        draggedTo: draggedTo,
        newStartDate: newStartDate,
      })
    }
  }

  const postSwitch = (key: string) => {
    if (key !== 'freePosts') {
      // AbotopicPosts
      const id = key
      let networks: NetworkType[] = []
      props.calendarData && props.calendarData[formatDate(new Date(day.year, day.month - 1, day.day).getTime(), 'dd.MM.yyyy')][key].published.map((individualPublished) => {
        if (networks.findIndex(elem => elem === individualPublished.publishedPost.networkType) === -1) {
          networks.push(individualPublished.publishedPost.networkType)
        }
      })
      props.calendarData && props.calendarData[formatDate(new Date(day.year, day.month - 1, day.day).getTime(), 'dd.MM.yyyy')][key].deferred.map((individualDeferred) => {
        if (networks.findIndex(elem => elem === individualDeferred.networkType) === -1) {
          networks.push(individualDeferred.networkType)
        }
      })
      const individualTemplate = props.calendarData && props.calendarData[formatDate(new Date(day.year, day.month - 1, day.day).getTime(), 'dd.MM.yyyy')][key].template
      if (individualTemplate) {
        networks = individualTemplate.allowedNetworks
      }
      const postMeta: PostObject | undefined = props.calendarData && props.calendarData[formatDate(new Date(day.year, day.month - 1, day.day).getTime(), 'dd.MM.yyyy')][key]
      const postContent: PostContent | undefined = postMeta?.postContent
      if (postContent) {
        return (
          <PostCard
            key={id}
            post={postContent}
            postMetaData={postMeta}
            id={id}
            networks={networks}
            published={new Date(day.year, day.month - 1, day.day) <= new Date()}
            day={day}
            position={position}
            dragAndDrop={dragAndDrop}
            setSelectedDate={props.setSelectedDate}
            editSettings={props.editSettings}
            onHandleDnD={props.onHandleDnD}
            calendarData={props.calendarData}
          />
        )
      }
    } else {
      const individiualPosts: any = []
      const individualPublished = props.calendarData && props.calendarData[formatDate(new Date(day.year, day.month - 1, day.day).getTime(), 'dd.MM.yyyy')][key].published
      const individualDeferred = props.calendarData && props.calendarData[formatDate(new Date(day.year, day.month - 1, day.day).getTime(), 'dd.MM.yyyy')][key].deferred

      if (individualPublished) {
        individualPublished.map((published) => {
          if (published.postContent) {
            const postMeta: PostObject | undefined = {
              published: [published],
              deferred: [],
              postContent: published.postContent,
            }
            individiualPosts.push(
              <PostCard
                key={published.publishedPost.id}
                id={published.publishedPost.id}
                post={published.postContent}
                postMetaData={postMeta}
                networks={[published.publishedPost.networkType]}
                published
                day={day}
                position={position}
                dragAndDrop={dragAndDrop}
                setSelectedDate={props.setSelectedDate}
                editSettings={props.editSettings}
                onHandleDnD={props.onHandleDnD}
                calendarData={props.calendarData}
              />)
          }
        })
      }

      if (individualDeferred) {
        individualDeferred.map((deferred) => {
          if (deferred.overridingPostContent) {
            const postMeta: PostObject | undefined = {
              published: [],
              deferred: [deferred],
              postContent: deferred.overridingPostContent,
            }
            individiualPosts.push(
              <PostCard
                key={deferred.id}
                post={deferred.overridingPostContent}
                id={deferred.id}
                networks={[deferred.networkType]}
                published={false}
                postMetaData={postMeta}
                day={day}
                position={position}
                dragAndDrop={dragAndDrop}
                setSelectedDate={props.setSelectedDate}
                editSettings={props.editSettings}
                onHandleDnD={props.onHandleDnD}
                calendarData={props.calendarData}
              />)
          }
        })
      }

      return individiualPosts
    }
  }

  const isDropArea = dragAndDrop.isDragging && dragAndDrop.draggedTo === position && dragAndDrop.draggedItemData?.postStart && !datesAreOnSameDay(new Date(dragAndDrop.draggedItemData?.postStart), new Date(position)) ? classes.dropArea : ''
  const isRestrictedArea = dragAndDrop.isDragging && isRestricted(position) ? classes.dropAreaRestricted : ''
  const isDropped = loadingCalendarData?.inProgress && dragAndDrop.draggedTo === position

  return (
    <Box
      key={position}
      data-position={position}
      className={`${classes.calendarDaysDay} ${isDropArea} ${isRestrictedArea}`}
      component='div'
      onDragOver={user?.role === 'USER' ? undefined : onDragOver}
    >
      <Typography variant='body2' component='span' className={classes.dayNumber}>{day.day}</Typography>
      <Box className={classes.postCards} component='div'>
        {props.calendarData && props.calendarData[formatDate(new Date(day.year, day.month - 1, day.day).getTime(), 'dd.MM.yyyy')] && (
          Object.keys(props.calendarData[formatDate(new Date(day.year, day.month - 1, day.day).getTime(), 'dd.MM.yyyy')])
            .map((key: string) => postSwitch(key))
        )}
        {isDropped && <Preloader size={24} thickness={2} minHeight='70px' />}
      </Box>
    </Box>
  )
}

export default CalendarDay
